import React, { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import HttpService from '../../services/httpService';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import Autocomplete from '@material-ui/lab/Autocomplete';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import HistoryIcon from '@material-ui/icons/History';
import CloseIcon from '@material-ui/icons/Close';

const useStyles = makeStyles(() => ({
  root: { padding: '20px' },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
  },
  title: { margin: 0, color: '#1C375D', fontWeight: 700 },
  subNote: { color: '#888', fontSize: 13, marginTop: 4 },
  filterBar: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 16,
    padding: '16px',
    marginBottom: 16,
  },
  dateField: { display: 'flex', flexDirection: 'column' },
  dateLabel: { fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: 0.5, marginBottom: 4 },
  itemsField: { flex: 1, minWidth: 260 },
  sortableHeader: { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' },
  sortIcon: { fontSize: 14, verticalAlign: 'middle', marginLeft: 4 },
  loadingContainer: { display: 'flex', justifyContent: 'center', padding: '40px' },
  positive: { color: '#2e7d32', fontWeight: 600 },
  negative: { color: '#c62828', fontWeight: 600 },
  clickableCell: {
    cursor: 'pointer',
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dialogTitleText: { margin: 0 },
  dialogSubNote: { color: '#888', fontSize: 13, marginTop: 4 },
}));

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#1C375D',
    color: '#fff',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  body: { fontSize: 14 },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  },
}))(TableRow);

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// "YYYY-MM-DD" from a Date's LOCAL calendar fields — not `.toISOString()`, which converts to UTC
// first and, for anyone east of UTC (e.g. India), can render as YESTERDAY's date for the first
// few hours of the local day. This keeps "today" matching what the user's clock actually shows.
const toDateInputValue = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Default date range is the current calendar month so far.
const firstDayOfCurrentMonth = () => {
  const d = new Date();
  d.setDate(1);
  return toDateInputValue(d);
};
const today = () => toDateInputValue(new Date());

// The ledger this page reports on only starts being meaningful from this date — nothing before
// it can be picked, in the calendar itself or by typing.
const MIN_STOCK_MOVEMENT_DATE = '2026-04-01';

const SUMMARY_COLUMNS = [
  { key: 'itemName', label: 'Item', align: 'left' },
  { key: 'opening', label: 'Opening', align: 'center' },
  { key: 'inwardRange', label: 'Inward', align: 'center' },
  { key: 'outwardRange', label: 'Outward', align: 'center' },
  { key: 'closingCalc', label: 'Closing', align: 'center' },
];

const downloadBlobFile = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const StockMovement = (props) => {
  const classes = useStyles();
  const { setLoading: setGlobalLoading, setSnackInfo } = props;

  // Raw (editable) date inputs vs the ones actually applied to the last fetch — filters here are
  // "Apply Filters"-driven (per the UI), not live-as-you-type, so a fetch only fires on Apply.
  const [fromDate, setFromDate] = useState(firstDayOfCurrentMonth());
  const [toDate, setToDate] = useState(today());
  const [appliedFromDate, setAppliedFromDate] = useState(firstDayOfCurrentMonth());
  const [appliedToDate, setAppliedToDate] = useState(today());

  const [itemInput, setItemInput] = useState('');
  const [itemOptions, setItemOptions] = useState([]);

  const [summarySelectedItems, setSummarySelectedItems] = useState([]); // [{_id, name}]
  const [summaryAppliedItems, setSummaryAppliedItems] = useState([]);

  const [sortBy, setSortBy] = useState('itemName');
  const [sortOrder, setSortOrder] = useState('asc');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Inward/Outward detail dialog — opened by clicking a Summary row's Inward or Outward figure.
  // Shows every individual physical movement behind that total: quantity, project, time, raised
  // by, approved by, and who did the inward/outward.
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailMaterialId, setDetailMaterialId] = useState(null);
  const [detailItemName, setDetailItemName] = useState('');
  const [detailType, setDetailType] = useState(null); // 'Inward' | 'Outward'
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchSeq = useRef(0);
  const detailFetchSeq = useRef(0);

  // Item search typeahead — server-side, debounced.
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const response = await HttpService.getStockMovementItems(itemInput);
        setItemOptions(response?.data?.data || []);
      } catch (error) {
        console.error('Error searching items:', error);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [itemInput]);

  const buildFilterParams = useCallback(() => ({
    startDate: appliedFromDate,
    endDate: appliedToDate,
    ...(summaryAppliedItems.length ? { itemIds: summaryAppliedItems.map(i => i._id).join(',') } : {}),
  }), [appliedFromDate, appliedToDate, summaryAppliedItems]);

  const fetchSummary = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setSummaryLoading(true);
    try {
      const response = await HttpService.getStockMovementSummary({
        ...buildFilterParams(),
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
      });
      if (fetchSeq.current !== seq) return; // a newer request already superseded this one
      if (response?.data?.success) {
        setRows(response.data.data || []);
        setTotalCount(response.data.pagination?.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching stock movement summary:', error);
      setSnackInfo?.("Couldn't load Stock Movement data", 'error');
    } finally {
      if (fetchSeq.current === seq) setSummaryLoading(false);
    }
  }, [buildFilterParams, page, rowsPerPage, sortBy, sortOrder, setSnackInfo]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const applyFilters = () => {
    // Backstops for anyone who types a date directly instead of using the picker (the picker's
    // own min/max already stop both of these through the calendar UI itself).
    if (fromDate < MIN_STOCK_MOVEMENT_DATE) {
      setSnackInfo?.(`Dates before ${MIN_STOCK_MOVEMENT_DATE} aren't available`, 'warning');
      return;
    }
    if (fromDate > toDate) {
      setSnackInfo?.('"From" date cannot be after "To" date', 'warning');
      return;
    }
    setAppliedFromDate(fromDate);
    setAppliedToDate(toDate);
    setSummaryAppliedItems(summarySelectedItems);
    setPage(0);
  };

  const resetFilters = () => {
    setFromDate(firstDayOfCurrentMonth());
    setToDate(today());
    setItemInput('');
    setSummarySelectedItems([]);
    setSummaryAppliedItems([]);
    setAppliedFromDate(firstDayOfCurrentMonth());
    setAppliedToDate(today());
    setPage(0);
  };

  const handleSort = (key) => {
    if (key === sortBy) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const handleDownloadCsv = async () => {
    setGlobalLoading(true);
    try {
      const response = await HttpService.getStockMovementSummary({
        ...buildFilterParams(),
        limit: -1,
        sortBy,
        sortOrder,
      });
      const data = response?.data?.data || [];
      const headers = ['Sr No', ...SUMMARY_COLUMNS.map(c => c.label)];
      const csvRows = data.map((r, idx) => [
        idx + 1,
        ...SUMMARY_COLUMNS.map(c => (c.key === 'itemName' ? `"${(r[c.key] || '').replace(/"/g, '""')}"` : r[c.key])),
      ]);
      const csvContent = [headers, ...csvRows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      downloadBlobFile(blob, `stock-movement-summary_${appliedFromDate}_to_${appliedToDate}.csv`);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setSnackInfo?.("Couldn't prepare CSV export", 'error');
    } finally {
      setGlobalLoading(false);
    }
  };

  // Every Inward/Outward transaction behind the applied filters, one block per item with a
  // running Opening -> Closing balance.
  const handleDownloadTransactionsExcel = async () => {
    setGlobalLoading(true);
    try {
      const response = await HttpService.downloadStockMovementTransactionsExcel(buildFilterParams());
      downloadBlobFile(response.data, `stock-movement-transactions_${appliedFromDate}_to_${appliedToDate}.xlsx`);
    } catch (error) {
      console.error('Error downloading transactions Excel:', error);
      setSnackInfo?.("Couldn't prepare transactions Excel", 'error');
    } finally {
      setGlobalLoading(false);
    }
  };

  const renderSortIcon = (key) => {
    if (sortBy !== key) return null;
    return sortOrder === 'asc' ? <ArrowUpwardIcon className={classes.sortIcon} /> : <ArrowDownwardIcon className={classes.sortIcon} />;
  };

  // Opens the detail dialog for one Summary row's Inward or Outward figure. Skips the round trip
  // entirely when there's nothing to show — a zero total has no movements behind it.
  const openDetail = (row, movementType) => {
    const total = movementType === 'Inward' ? row.inwardRange : row.outwardRange;
    if (!total) return;
    setDetailMaterialId(row.materialId);
    setDetailItemName(row.itemName);
    setDetailType(movementType);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailRows([]);
  };

  useEffect(() => {
    if (!detailOpen || !detailMaterialId || !detailType) return;
    const seq = ++detailFetchSeq.current;
    setDetailLoading(true);
    HttpService.getStockMovementDetail({
      materialId: detailMaterialId,
      movementType: detailType,
      startDate: appliedFromDate,
      endDate: appliedToDate,
    }).then((response) => {
      if (detailFetchSeq.current !== seq) return;
      if (response?.data?.success) setDetailRows(response.data.data || []);
    }).catch((error) => {
      console.error('Error fetching stock movement detail:', error);
      setSnackInfo?.("Couldn't load movement detail", 'error');
    }).finally(() => {
      if (detailFetchSeq.current === seq) setDetailLoading(false);
    });
  }, [detailOpen, detailMaterialId, detailType, appliedFromDate, appliedToDate, setSnackInfo]);

  const handleDownloadDetailExcel = async () => {
    setGlobalLoading(true);
    try {
      const response = await HttpService.downloadStockMovementDetailExcel({
        materialId: detailMaterialId,
        movementType: detailType,
        startDate: appliedFromDate,
        endDate: appliedToDate,
      });
      downloadBlobFile(response.data, `stock-movement-${detailType.toLowerCase()}_${detailItemName || 'item'}_${appliedFromDate}_to_${appliedToDate}.xlsx`);
    } catch (error) {
      console.error('Error downloading detail Excel:', error);
      setSnackInfo?.("Couldn't prepare Excel export", 'error');
    } finally {
      setGlobalLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerRow}>
        <div>
          <h2 className={classes.title}>Item-level Stock Movement</h2>
          <div className={classes.subNote}>{totalCount} item(s) · click a column to sort · click an Inward/Outward figure for its detail</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<HistoryIcon />}
            style={{ background: '#FF8C00', color: '#fff', textTransform: 'none' }}
            onClick={() => window.open('/stock-movement-logs', '_blank', 'noopener,noreferrer')}
          >
            Stock Movement Logs
          </Button>
          <Button variant="contained" style={{ background: '#2e7d32', color: '#fff' }} startIcon={<GetAppIcon />} onClick={handleDownloadCsv}>Download Table CSV</Button>
        </div>
      </div>

      <Paper className={classes.filterBar}>
        <div className={classes.dateField}>
          <span className={classes.dateLabel}>FROM</span>
          <TextField
            type="date" size="small" variant="outlined" value={fromDate}
            inputProps={{ min: MIN_STOCK_MOVEMENT_DATE, max: toDate < today() ? toDate : today() }}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <span style={{ marginBottom: 8 }}>→</span>
        <div className={classes.dateField}>
          <span className={classes.dateLabel}>TO</span>
          <TextField
            type="date" size="small" variant="outlined" value={toDate}
            inputProps={{ min: fromDate > MIN_STOCK_MOVEMENT_DATE ? fromDate : MIN_STOCK_MOVEMENT_DATE, max: today() }}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div className={classes.itemsField}>
          <span className={classes.dateLabel}>ITEMS (multi-select)</span>
          <Autocomplete
            multiple
            size="small"
            value={summarySelectedItems}
            onChange={(e, newValue) => setSummarySelectedItems(newValue || [])}
            onInputChange={(e, newInput) => setItemInput(newInput)}
            options={itemOptions}
            getOptionLabel={(option) => option?.name || ''}
            getOptionSelected={(option, value) => option._id === value._id}
            renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Search & select items..." />}
          />
        </div>
        <Button variant="contained" style={{ background: '#1C375D', color: '#fff' }} onClick={applyFilters}>Apply Filters</Button>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={resetFilters}>Reset</Button>
             <Button
            variant="contained"
            startIcon={<GetAppIcon />}
            style={{ background: '#2e7d32', color: '#fff', textTransform: 'none' }}
            onClick={handleDownloadTransactionsExcel}
          >
            Download Transaction Data
          </Button>
      </Paper>

      {summaryLoading ? (
        <div className={classes.loadingContainer}><CircularProgress /></div>
      ) : (
        <TableContainer component={Paper}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">SR NO.</StyledTableCell>
                {SUMMARY_COLUMNS.map(col => (
                  <StyledTableCell
                    key={col.key}
                    align={col.align || 'center'}
                    className={classes.sortableHeader}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label.toUpperCase()}{renderSortIcon(col.key)}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={SUMMARY_COLUMNS.length + 1} align="center" style={{ padding: 30, color: '#888' }}>
                    No items found for the selected filters
                  </TableCell>
                </TableRow>
              ) : rows.map((row, idx) => (
                <StyledTableRow key={row.materialId}>
                  <TableCell align="center">{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{row.itemName}</TableCell>
                  <TableCell align="center">{fmt(row.opening)}</TableCell>
                  <TableCell
                    align="center"
                    className={`${row.inwardRange > 0 ? classes.positive : ''} ${row.inwardRange ? classes.clickableCell : ''}`}
                    onClick={() => openDetail(row, 'Inward')}
                    title={row.inwardRange ? 'Click to see inward detail' : undefined}
                  >
                    {row.inwardRange > 0 ? `+${fmt(row.inwardRange)}` : fmt(row.inwardRange)}
                  </TableCell>
                  <TableCell
                    align="center"
                    className={`${row.outwardRange > 0 ? classes.negative : ''} ${row.outwardRange ? classes.clickableCell : ''}`}
                    onClick={() => openDetail(row, 'Outward')}
                    title={row.outwardRange ? 'Click to see outward detail' : undefined}
                  >
                    {row.outwardRange > 0 ? `-${fmt(row.outwardRange)}` : fmt(row.outwardRange)}
                  </TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>{fmt(row.closingCalc)}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100, { label: 'All', value: totalCount || 1 }]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </TableContainer>
      )}

      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="lg" fullWidth>
        <DialogTitle disableTypography>
          <div className={classes.dialogHeader}>
            <div>
              <h3 className={classes.dialogTitleText}>{detailType} Detail — {detailItemName}</h3>
              <div className={classes.dialogSubNote}>{appliedFromDate} to {appliedToDate} · {detailRows.length} movement(s)</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                variant="contained"
                size="small"
                // Only inline the green when enabled — otherwise it'd sit on top of Material-UI's
                // own disabled styling and still look clickable while it isn't.
                style={(detailLoading || detailRows.length === 0) ? undefined : { background: '#2e7d32', color: '#fff' }}
                startIcon={<GetAppIcon />}
                onClick={handleDownloadDetailExcel}
                disabled={detailLoading || detailRows.length === 0}
              >
                Download Excel
              </Button>
              <IconButton size="small" onClick={closeDetail}><CloseIcon /></IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <div className={classes.loadingContainer}><CircularProgress /></div>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell align="center">SR NO.</StyledTableCell>
                    <StyledTableCell>ORDER ID</StyledTableCell>
                    <StyledTableCell align="center">QUANTITY</StyledTableCell>
                    <StyledTableCell>PROJECT</StyledTableCell>
                    <StyledTableCell>RAISED BY</StyledTableCell>
                    <StyledTableCell>APPROVED BY</StyledTableCell>
                    <StyledTableCell>{detailType === 'Outward' ? 'OUTWARD BY' : 'INWARD BY'}</StyledTableCell>
                    <StyledTableCell>DATE & TIME</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" style={{ padding: 30, color: '#888' }}>
                        No movement records found for this range
                      </TableCell>
                    </TableRow>
                  ) : detailRows.map((row, idx) => (
                    <StyledTableRow key={row._id}>
                      <TableCell align="center">{idx + 1}</TableCell>
                      <TableCell>{row.orderId}</TableCell>
                      <TableCell align="center" className={detailType === 'Outward' ? classes.negative : classes.positive}>
                        {detailType === 'Outward' ? `-${fmt(row.quantity)}` : `+${fmt(row.quantity)}`}
                      </TableCell>
                      <TableCell>{row.project}</TableCell>
                      <TableCell>{row.raisedBy || '—'}</TableCell>
                      <TableCell>{row.approvedBy || '—'}</TableCell>
                      <TableCell>{row.movedBy || '—'}</TableCell>
                      <TableCell>{row.time ? new Date(row.time).toLocaleString('en-IN') : '—'}</TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default connect(null, { setLoading, setSnackInfo })(StockMovement);
